import { anyOverlap } from "./glob.ts";
import type { Actor, RuleEffect, Tier } from "./types.ts";
import type { Verdict } from "./verification.ts";

/** A person's standing instruction for their own agent to decide reviews alone. */
export type DelegationPolicy = {
  /** An agent the person owns. */
  agentId: string;
  /** Tiers the agent may decide alone. `critical` is rejected at write time (see `validateDelegationPolicy`). */
  tiers: Tier[];
  /** Globs the agent may decide alone in. Empty = any area not held back by a hard rule. */
  areas: string[];
  /** Decision kinds in scope. Defaults to `["review_change"]`; `spot_check` is opt-in. */
  kinds: ("review_change" | "spot_check")[];
};

export const DEFAULT_DELEGATION_TIERS: Tier[] = ["low", "medium"];
export const DELEGABLE_TIERS: Tier[] = ["low", "medium", "high"]; // never "critical"

/**
 * Which fired rule effects keep a human reviewer whatever the policy says. A `Record` over every
 * `RuleEffect` (not a hand-picked array) so a new effect added to `types.ts` fails to compile here
 * until someone decides whether it is a hard rule — `min_tier` is deliberately `false` (see §4.2).
 */
const HARD_RULE_EFFECTS: Record<RuleEffect, boolean> = {
  forbid_agent_builder: true,
  require_human_reviewer: true,
  require_focus: true,
  require_reviewer: true,
  min_tier: false,
};

export type DelegationBlock =
  | "no_policy"
  | "kind_not_delegated"
  | "agent_inactive"
  | "agent_not_owned"
  | "builder_is_delegate"
  | "tier_critical"
  | "tier_not_in_policy"
  | "area_not_in_policy"
  | "hard_rule"
  | "verdict_inconclusive"
  | "tests_weakened"
  | "shares_owner_with_builder";

export type DelegationOutcome = {
  /** The agent that should run the review. Null = nothing changes from today: no run, decision behaves as it always has. */
  delegateId: string | null;
  /** Where the delegate came from: the assignee's own policy, or the project's/office's reviewer agent (landing C1). */
  source?: "person" | "project";
  /** True only when this agent's verdict may answer the decision on its own. */
  mayDecideAlone: boolean;
  /** Every reason the agent may not decide alone, in rule order. Empty iff mayDecideAlone. */
  blocks: DelegationBlock[];
  /** One sentence for the timeline and the Bridge card, naming the first (most specific) block. */
  why: string;
};

const BLOCK_WHY: Record<DelegationBlock, string> = {
  no_policy: "no delegation policy is set for this person",
  kind_not_delegated: "this decision kind is not in the person's delegation policy",
  agent_inactive: "the delegate agent is inactive",
  agent_not_owned: "the delegate agent is not owned by this person",
  builder_is_delegate: "an agent never reviews a change it built",
  tier_critical: "critical risk always needs two human reviews",
  tier_not_in_policy: "this risk tier is outside the person's delegation policy",
  area_not_in_policy: "this task's areas are outside the person's delegation policy",
  hard_rule: "a fired rule requires a human reviewer",
  verdict_inconclusive: "the verification verdict was inconclusive; a human must look",
  tests_weakened: "the submitted evidence weakened tests",
  shares_owner_with_builder: "the delegate shares an owner with the agent that built this high-risk change",
};

function noRun(block: DelegationBlock): DelegationOutcome {
  return { delegateId: null, mayDecideAlone: false, blocks: [block], why: BLOCK_WHY[block] };
}

/**
 * Decides whether a review run should happen at all (`delegateId`), and — only once one does —
 * whether the agent's verdict may answer the decision alone (`mayDecideAlone`). Pure: every input
 * is already resolved (fired rule effects, the evidence verdict, etc.), so this has no I/O.
 */
export function resolveDelegation(input: {
  policy: DelegationPolicy | null;
  kind: "review_change" | "spot_check";
  delegate: { id: string; active: boolean; ownerId: string | null } | null;
  assigneeId: string;
  builderId: string | null;
  /** Owner of the builder, when the builder is an agent. Null for a human builder. */
  builderOwnerId: string | null;
  tier: Tier;
  taskAreas: string[];
  firedRuleEffects: RuleEffect[];
  verdict: Verdict;
  testsWeakened: boolean;
  /**
   * Landing C1: `project` means the delegate is the project's (or office's) reviewer agent, not one the
   * assignee owns. Ownership and the person's policy do not apply; `projectTiers` says what it may decide
   * alone. The code-safety blocks (critical, hard rule, inconclusive, weakened tests, builder) still do.
   */
  source?: "person" | "project";
  projectTiers?: readonly Tier[];
}): DelegationOutcome {
  const { policy } = input;
  if (input.source === "project") return resolveProjectDelegation(input);

  // Whether a review run happens at all. Any failure here means no run and the decision
  // behaves exactly as it does today — there is nothing further to evaluate.
  if (!policy) return noRun("no_policy");
  if (!policy.kinds.includes(input.kind)) return noRun("kind_not_delegated");
  if (!input.delegate || !input.delegate.active) return noRun("agent_inactive");
  if (input.delegate.ownerId !== input.assigneeId) return noRun("agent_not_owned");
  if (input.builderId !== null && input.delegate.id === input.builderId) return noRun("builder_is_delegate");

  const delegateId = input.delegate.id;
  const blocks: DelegationBlock[] = [];

  if (input.tier === "critical") blocks.push("tier_critical");
  if (!policy.tiers.includes(input.tier)) blocks.push("tier_not_in_policy");
  if (policy.areas.length > 0 && !anyOverlap(input.taskAreas, policy.areas)) blocks.push("area_not_in_policy");
  if (input.firedRuleEffects.some((effect) => HARD_RULE_EFFECTS[effect])) blocks.push("hard_rule");
  if (input.verdict === "inconclusive") blocks.push("verdict_inconclusive");
  if (input.testsWeakened) blocks.push("tests_weakened");
  if (input.tier === "high" && input.builderOwnerId !== null && input.builderOwnerId === input.delegate.ownerId) {
    blocks.push("shares_owner_with_builder");
  }

  return {
    delegateId,
    source: "person",
    mayDecideAlone: blocks.length === 0,
    blocks,
    why: blocks.length === 0 ? "in policy; the agent may decide alone" : BLOCK_WHY[blocks[0]!],
  };
}

function resolveProjectDelegation(input: Parameters<typeof resolveDelegation>[0]): DelegationOutcome {
  if (!input.delegate || !input.delegate.active) return noRun("agent_inactive");
  if (input.builderId !== null && input.delegate.id === input.builderId) return noRun("builder_is_delegate");
  const blocks: DelegationBlock[] = [];
  if (input.tier === "critical") blocks.push("tier_critical");
  if (!(input.projectTiers ?? []).includes(input.tier)) blocks.push("tier_not_in_policy");
  if (input.firedRuleEffects.some((effect) => HARD_RULE_EFFECTS[effect])) blocks.push("hard_rule");
  if (input.verdict === "inconclusive") blocks.push("verdict_inconclusive");
  if (input.testsWeakened) blocks.push("tests_weakened");
  return {
    delegateId: input.delegate.id,
    source: "project",
    mayDecideAlone: blocks.length === 0,
    blocks,
    why: blocks.length === 0 ? "inside the project's review policy; the office reviewer may decide alone" : PROJECT_BLOCK_WHY[blocks[0]!] ?? BLOCK_WHY[blocks[0]!],
  };
}

/** The project-source wording where the person-policy one would name "the person's delegation policy". */
const PROJECT_BLOCK_WHY: Partial<Record<DelegationBlock, string>> = {
  tier_not_in_policy: "this risk tier is above what the project lets the office reviewer decide alone",
};

/**
 * Readable problems with a candidate policy, shared by the server write path and the UI editor
 * so neither re-derives these rules. Returns an empty list for a valid policy.
 */
export function validateDelegationPolicy(
  policy: DelegationPolicy,
  context: { actor: Actor; agent: Actor | null },
): string[] {
  const problems: string[] = [];
  const { actor, agent } = context;

  if (actor.kind !== "human") problems.push("only a person can hold a delegation policy");

  if (!agent) {
    problems.push("delegationPolicy.agentId does not refer to an existing agent");
  } else {
    if (agent.kind !== "agent") problems.push("delegationPolicy.agentId must refer to an agent, not a person");
    if (agent.ownerId !== actor.id) problems.push("the agent is not owned by this person");
    if (!agent.active) problems.push("the agent is inactive");
  }

  if (policy.tiers.length === 0) problems.push("delegationPolicy.tiers must not be empty");
  if (policy.tiers.some((tier) => !DELEGABLE_TIERS.includes(tier))) {
    problems.push("delegationPolicy.tiers must not include critical; critical always needs a human");
  }
  if (policy.kinds.length === 0) problems.push("delegationPolicy.kinds must not be empty");

  return problems;
}
